import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users, Calendar, CheckCircle, XCircle, Clock, BookOpen, GraduationCap, Trash2, Plus, Copy, AlertCircle, AlertTriangle, Upload, Phone, Mail, Link, RotateCcw, Send, UserMinus, UserPlus, Ban, MessageSquare, PlayCircle, ArrowUpCircle, Accessibility, UserCheck } from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';
import { getSensors, assignSensorToCourse, unassignSensor, HabilitetConflictError, type Sensor } from '@/lib/admin';
import { parseAssessmentFormShort, getAssessmentFormBadgeClass } from '@/lib/assessmentForm';
import { useToast, useConfirm } from './ui';
import { BroadcastModal } from './BroadcastModal';
import AccommodationManager from './AccommodationManager';
import CompositeAssessmentMatrix from './CompositeAssessmentMatrix';
import CompositePlanEditor from './CompositePlanEditor';
import FinalizeAssessmentModal from './FinalizeAssessmentModal';
import CandidateApprovalsPanel from './CandidateApprovalsPanel';
import HelpTooltip from './help/HelpTooltip';

interface CourseInstance {
  id: string;
  name: string;
  exam_set_id: string;
  exam_title: string;
  study_plan_title: string;
  member_id: string;
  organization_name: string;
  start_date: string | null;
  end_date: string | null;
  max_candidates: number | null;
  status: string;
  // v6.6.2: api_enabled/api_link fjernet
  approval_mode?: 'auto' | 'hybrid' | 'manual';
  created_at: string;
  // Task #192: Modul-omfang fra exam_set
  exam_set_included_module_ids?: string[] | null;
  included_modules?: Array<{
    id: string;
    code: string;
    title: string;
    ects_credits: number | null;
    estimated_hours: number | null;
  }>;
}

interface ExamSession {
  id: string;
  anonymous_id: string;
  unique_candidate_id: string;
  candidate_code: string | null;
  started_at: string;
  submitted_at: string | null;
  status: string;
  grade: string | null;
  percentage: number | null;
  passed: boolean | null;
}

interface CourseSensor {
  id: string;
  sensor_user_id: string;
  sensor_name: string;
  sensor_email: string;
  role: string;
  assigned_at: string;
}

interface CourseCandidate {
  id: string;
  full_name: string;
  date_of_birth: string;
  mobile_number: string;
  email: string | null;
  candidate_code: string;
  status: string;
  exam_session_id: string | null;
  imported_via: string;
  created_at: string;
  exam_started_at: string | null;
  exam_submitted_at: string | null;
  graded_at: string | null;
  grade: string | null;
  percentage: number | null;
  passed: boolean | null;
  // v6.3.5: sensur-detaljer
  alternative_assessment?: boolean;
  alternative_assessment_note?: string | null;
  sensor_comment?: string | null;
  sensor_name?: string | null;
  sensored_at?: string | null;
  // Invitation fields
  invitation_token: string | null;
  invitation_sent_at: string | null;
  invitation_email_sent: boolean;
  invitation_sms_sent: boolean;
  excluded: boolean;
  excluded_at: string | null;
  excluded_reason: string | null;
  // Påmelding / venteliste / oppmøte
  waitlist_position: number | null;
  attendance_marked: boolean | null;
  attendance_marked_at: string | null;
  registration_source: string | null;
  payment_status: string | null;
  self_cancelled_at: string | null;
  moved_from_instance_id: string | null;
  // Task #139: SEB-test-status
  seb_test_completed_at?: string | null;
  seb_test_invited?: boolean;
}

export default function CourseInstanceDetail() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = getUser();

  // Les ?tab=... fra URL ved mount; whitelistet mot gyldige verdier for å unngå
  // ugyldig state. Dette gjør det mulig å dyplenke til f.eks. ?tab=candidates
  // fra /courses-and-exams "Til sensur"-rad.
  const VALID_TABS = ['overview', 'candidates', 'exam', 'sensors', 'composite', 'plan'] as const;
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return (t && (VALID_TABS as readonly string[]).includes(t) ? t : 'overview') as typeof VALID_TABS[number];
  })();
  // Les ?filter=... (brukes av Kandidater-tab for å filtrere på f.eks.
  // kandidater som venter sensur). Nullstilles når filteret fjernes via UI.
  const initialCandidateFilter = (searchParams.get('filter') || null) as 'awaiting_sensur' | null;

  const [course, setCourse] = useState<CourseInstance | null>(null);
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [candidates, setCandidates] = useState<CourseCandidate[]>([]);
  const [courseSensors, setCourseSensors] = useState<CourseSensor[]>([]);
  const [availableSensors, setAvailableSensors] = useState<Sensor[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'candidates' | 'exam' | 'sensors' | 'composite' | 'plan'>(initialTab);
  const [candidateFilter, setCandidateFilter] = useState<'awaiting_sensur' | null>(initialCandidateFilter);
  const [compositePlanInfo, setCompositePlanInfo] = useState<{ is_composite: boolean; total_components: number } | null>(null);
  const [finalizingCandidateId, setFinalizingCandidateId] = useState<string | null>(null);

  // Broadcast / kommunikasjon
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  // Tilrettelegging (extra tid m.m.) per kandidat-sesjon
  const [accommodationCandidate, setAccommodationCandidate] = useState<{ sessionId?: string; candidateId?: string; name: string } | null>(null);
  const [expandedSensurCandidate, setExpandedSensurCandidate] = useState<string | null>(null);  // v6.3.5

  // Oppmøte og start-eksamen (Prøve-tab)
  const [savingAttendance, setSavingAttendance] = useState<string | null>(null);
  const [startingExam, setStartingExam] = useState(false);
  const [promotingCandidate, setPromotingCandidate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddSensorModal, setShowAddSensorModal] = useState(false);
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // v6.2.4: habilitet-bypass-dialog med valgfri begrunnelse-textarea
  const [habilityDialog, setHabilityDialog] = useState<{
    open: boolean;
    message: string;
    blocked: boolean;
    requiresReason: boolean;
    reasonMinLength: number;
    reasonPrompt?: string;
    fagligConfirmationNotice?: string;
  }>({ open: false, message: '', blocked: false, requiresReason: false, reasonMinLength: 0 });
  const [habilityReason, setHabilityReason] = useState('');

  // Candidate registration state
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [candidateFormData, setCandidateFormData] = useState({
    full_name: '',
    date_of_birth: '',
    mobile_number: '',
    email: ''
  });
  const [registeredCandidate, setRegisteredCandidate] = useState<{
    id: string;
    full_name: string;
    candidate_code: string;
    exam_url: string;
  } | null>(null);
  const [registering, setRegistering] = useState(false);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [csvImportResult, setCsvImportResult] = useState<{
    imported: number;
    failed: number;
    errors: Array<{ line: number; error: string }>;
  } | null>(null);
  const [importingCsv, setImportingCsv] = useState(false);

  // Reset exam session state
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [showResetFinalConfirmModal, setShowResetFinalConfirmModal] = useState(false);
  const [candidateToReset, setCandidateToReset] = useState<CourseCandidate | null>(null);
  const [resetReason, setResetReason] = useState('');  // v6.3.5: begrunnelse påkrevd
  const [resettingSession, setResettingSession] = useState(false);

  // Overfør til sensor for manuell vurdering (no-show + prosjekt/muntlig levert)
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [candidateToTransfer, setCandidateToTransfer] = useState<CourseCandidate | null>(null);
  const [transferReason, setTransferReason] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Invitation state (handleSendAllInvitations + handleResendIndividual lever videre for evt. enkelt-utsendelser)
  const [sendingIndividualInvite, setSendingIndividualInvite] = useState<string | null>(null);
  const [excludingCandidate, setExcludingCandidate] = useState<string | null>(null);
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [candidateToExclude, setCandidateToExclude] = useState<CourseCandidate | null>(null);
  const [excludeReason, setExcludeReason] = useState('');

  // Check if user can manage candidates (systemadmin, admin, or member)
  const canManageCandidates = currentUser && ['systemadmin', 'admin', 'member'].includes(currentUser.role);

  useEffect(() => {
    if (courseId) {
      loadData();
    }
  }, [courseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load course details
      const courseRes = await authenticatedFetch(`/api/admin/course-instances/${courseId}`);
      const courseData = await courseRes.json();

      if (!courseData.success) {
        throw new Error(courseData.message || 'Failed to load course');
      }

      setCourse(courseData.instance);

      // Load sessions (exam sessions)
      const sessionsRes = await authenticatedFetch(`/api/admin/course-instances/${courseId}/sessions`);
      const sessionsData = await sessionsRes.json();

      if (sessionsData.success) {
        setSessions(sessionsData.sessions || []);
      }

      // Load registered candidates (from course_candidates table)
      const candidatesRes = await authenticatedFetch(`/api/admin/course-instances/${courseId}/candidates`);
      const candidatesData = await candidatesRes.json();

      if (candidatesData.success) {
        setCandidates(candidatesData.candidates || []);
      }

      // Load course sensors
      const sensorsRes = await authenticatedFetch(`/api/admin/course-instances/${courseId}/sensors`);
      const sensorsData = await sensorsRes.json();

      if (sensorsData.success) {
        setCourseSensors(sensorsData.sensors || []);
      }

      // Load available sensors for this organization
      if (courseData.instance?.member_id) {
        const allSensors = await getSensors({ member_id: courseData.instance.member_id });
        setAvailableSensors(allSensors);
      }

      // Blokk 5 T1.17 — sjekk om planen er kompositt for å vise «Sensur-oversikt»-tab
      try {
        const planRes = await authenticatedFetch(`/api/admin/assessment-plans/${courseId}/matrix`);
        if (planRes.ok) {
          const planData = await planRes.json();
          setCompositePlanInfo({
            is_composite: !!planData.is_composite,
            total_components: planData.components?.length || 0
          });
        }
      } catch {
        // Silent — matrise-tab bare skjules
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSensor = async (confirmHabilitet: boolean = false, reason?: string) => {
    if (!selectedSensorId || !courseId) return;

    try {
      setAssigning(true);
      await assignSensorToCourse({
        course_instance_id: courseId,
        sensor_user_id: selectedSensorId,
        assigned_by: currentUser.id,
        confirmHabilitet,
        habilitetReason: reason
      });

      toast.showSuccess('Sensor tildelt!');
      setShowAddSensorModal(false);
      setSelectedSensorId('');
      setHabilityReason('');
      setHabilityDialog({ open: false, message: '', blocked: false, requiresReason: false, reasonMinLength: 0 });
      await loadData();
    } catch (err: any) {
      // v6.2.4: Håndter habilitet-konflikt med skjerpet regelsett.
      //   - blocked=true → vis blokkerings-melding, ikke bypass mulig
      //   - blocked=false + canBypass=true → åpne dialog med evt. begrunnelse-felt
      if (err instanceof HabilitetConflictError) {
        setAssigning(false);
        setHabilityReason('');
        setHabilityDialog({
          open: true,
          message: err.message,
          blocked: err.blocked,
          requiresReason: err.requiresReason,
          reasonMinLength: err.reasonMinLength,
          reasonPrompt: err.reasonPrompt,
          fagligConfirmationNotice: err.fagligConfirmationNotice
        });
        return;
      }
      toast.showError(`Feil ved tildeling: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  const handleHabilityConfirm = async () => {
    if (habilityDialog.requiresReason && habilityReason.trim().length < habilityDialog.reasonMinLength) {
      toast.showWarning(`Begrunnelse må være minst ${habilityDialog.reasonMinLength} tegn.`);
      return;
    }
    const reason = habilityDialog.requiresReason ? habilityReason.trim() : undefined;
    setHabilityDialog({ ...habilityDialog, open: false });
    await handleAssignSensor(true, reason);
  };

  const handleHabilityCancel = () => {
    setHabilityDialog({ open: false, message: '', blocked: false, requiresReason: false, reasonMinLength: 0 });
    setHabilityReason('');
  };

  const handleUnassignSensor = async (assignmentId: string, sensorName: string) => {
    const confirmed = await confirm({
      title: 'Fjern sensor',
      message: `Fjerne ${sensorName} fra kurset?`,
      confirmLabel: 'Fjern',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      await unassignSensor(assignmentId);
      toast.showSuccess('Sensor fjernet');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved fjerning: ${err.message}`);
    }
  };

  const handleRegisterCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId) return;

    // Validate required fields
    if (!candidateFormData.full_name.trim()) {
      toast.showWarning('Fullt navn er påkrevd');
      return;
    }
    if (!candidateFormData.date_of_birth) {
      toast.showWarning('Fødselsdato er påkrevd');
      return;
    }
    if (!candidateFormData.mobile_number.trim()) {
      toast.showWarning('Mobilnummer er påkrevd');
      return;
    }
    if (!candidateFormData.email.trim()) {
      toast.showWarning('E-post er påkrevd for å sende invitasjon');
      return;
    }

    try {
      setRegistering(true);
      const response = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/register-candidate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: candidateFormData.full_name.trim(),
            date_of_birth: candidateFormData.date_of_birth,
            mobile_number: candidateFormData.mobile_number.trim(),
            email: candidateFormData.email.trim() || undefined
          })
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to register candidate');
      }

      setRegisteredCandidate(data.candidate);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved registrering: ${err.message}`);
    } finally {
      setRegistering(false);
    }
  };

  const handleCloseRegistrationModal = () => {
    setShowAddCandidateModal(false);
    setRegisteredCandidate(null);
    setCandidateFormData({ full_name: '', date_of_birth: '', mobile_number: '', email: '' });
  };

  const handleCsvImport = async () => {
    if (!courseId || !csvData.trim()) return;

    try {
      setImportingCsv(true);
      const response = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/import-csv`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ csv_data: csvData })
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'CSV import failed');
      }

      setCsvImportResult({
        imported: data.imported,
        failed: data.failed,
        errors: data.results?.errors || []
      });
      await loadData();
    } catch (err: any) {
      toast.showError(`CSV import feilet: ${err.message}`);
    } finally {
      setImportingCsv(false);
    }
  };

  const handleCloseCsvModal = () => {
    setShowCsvImportModal(false);
    setCsvData('');
    setCsvImportResult(null);
  };

  // Oppmøte: marker en kandidat som tilstede / ikke
  const handleToggleAttendance = async (candidateId: string, marked: boolean) => {
    if (!courseId) return;
    try {
      setSavingAttendance(candidateId);
      const res = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/${candidateId}/attendance`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ marked })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Kunne ikke oppdatere oppmøte');
      }
      await loadData();
    } catch (err) {
      toast.showError(err instanceof Error ? err.message : 'Oppmøte-feil');
    } finally {
      setSavingAttendance(null);
    }
  };

  const handleBulkAttendance = async (candidateIds: string[], marked: boolean) => {
    if (!courseId || candidateIds.length === 0) return;
    try {
      const res = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/attendance/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidate_ids: candidateIds, marked })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Bulk-oppmøte feilet');
      toast.showSuccess(`${candidateIds.length} kandidater oppdatert`);
      await loadData();
    } catch (err) {
      toast.showError(err instanceof Error ? err.message : 'Bulk-feil');
    }
  };

  const handleStartExamForAttendees = async () => {
    if (!courseId) return;
    const attendeesCount = candidates.filter(
      c => c.attendance_marked === true && !c.excluded && (c.status === 'registered' || c.status === 'invited')
    ).length;
    const noShowsCount = candidates.filter(
      c => c.attendance_marked !== true && !c.excluded && (c.status === 'registered' || c.status === 'invited')
    ).length;

    if (attendeesCount === 0) {
      toast.showWarning('Hak av oppmøte for minst én kandidat før eksamen kan startes.');
      return;
    }

    const confirmed = await confirm({
      title: 'Start eksamen for fremmøtte',
      message: `Sender SEB-eksamenslenke til ${attendeesCount} fremmøtte kandidater.${noShowsCount > 0 ? `\n\n${noShowsCount} kandidater er ikke huket av som fremmøtt — de får status "ikke møtt" og kan flyttes til ny instans senere.` : ''}\n\nDenne handlingen kan ikke angres.`,
      confirmLabel: 'Start eksamen',
      cancelLabel: 'Avbryt'
    });
    if (!confirmed) return;

    try {
      setStartingExam(true);
      const res = await authenticatedFetch(`/api/admin/course-instances/${courseId}/start-exam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Kunne ikke starte eksamen');
      toast.showSuccess(`Eksamen startet: ${data.emails_sent} e-poster + ${data.sms_sent} SMS sendt til ${data.attendees_count} fremmøtte.`);
      await loadData();
    } catch (err) {
      toast.showError(err instanceof Error ? err.message : 'Start-eksamen feilet');
    } finally {
      setStartingExam(false);
    }
  };

  /**
   * Send invitasjon PÅ FORHÅND — meldingen henviser til planlagt eksamens-tidspunkt.
   * Lenken er sperret av backend til exam_window_start.
   */
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const handleSendInvitations = async (candidateIds: string[]) => {
    if (!courseId) return;
    if (candidateIds.length === 0) {
      toast.showWarning('Hak av minst én kandidat før du sender invitasjon.');
      return;
    }
    const examStartStr = (course as any)?.exam_window_start
      ? new Date((course as any).exam_window_start).toLocaleString('no-NO',
          { dateStyle: 'long', timeStyle: 'short' })
      : null;
    if (!examStartStr) {
      toast.showWarning('Sett eksamens-tidspunkt på kurset før du sender invitasjoner på forhånd.');
      return;
    }
    const confirmed = await confirm({
      title: 'Send invitasjon på forhånd',
      message: `Sender e-post og SMS til ${candidateIds.length} valgte kandidat(er) med planlagt eksamens-tidspunkt:\n\n${examStartStr}\n\nLenken er sperret til dette tidspunktet — kandidatene får melding om at prøven ikke er åpen ennå hvis de klikker for tidlig.`,
      confirmLabel: 'Send invitasjon',
      cancelLabel: 'Avbryt'
    });
    if (!confirmed) return;
    try {
      setSendingInvitations(true);
      const res = await authenticatedFetch(`/api/admin/course-instances/${courseId}/send-invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: candidateIds })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Kunne ikke sende invitasjon');
      toast.showSuccess(`Invitasjon sendt: ${data.emails_sent} e-post + ${data.sms_sent} SMS til ${data.candidates_count} kandidat(er).`);
      await loadData();
    } catch (err) {
      toast.showError(err instanceof Error ? err.message : 'Send-invitasjon feilet');
    } finally {
      setSendingInvitations(false);
    }
  };

  const handlePromoteFromWaitlist = async (candidateId: string) => {
    if (!courseId) return;
    const confirmed = await confirm({
      title: 'Promoter fra venteliste',
      message: 'Flytt denne kandidaten opp fra venteliste til påmeldt? Kandidaten får bekreftelses-e-post og SMS.',
      confirmLabel: 'Promoter',
      cancelLabel: 'Avbryt'
    });
    if (!confirmed) return;
    try {
      setPromotingCandidate(candidateId);
      const res = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/${candidateId}/promote-from-waitlist`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Promotering feilet');
      toast.showSuccess('Kandidat flyttet fra venteliste til påmeldt.');
      await loadData();
    } catch (err) {
      toast.showError(err instanceof Error ? err.message : 'Promotering feilet');
    } finally {
      setPromotingCandidate(null);
    }
  };

  // Invitation handlers
  // Den gamle "Send invitasjoner til alle"-flyten er erstattet av Prøve-tabbens
  // "Start eksamen for fremmøtte". Backend-endepunktet /api/exam-invitations/.../send-all
  // er fortsatt operativt for evt. ekstern bruk men kalles ikke fra UI lenger.

  const handleSendIndividualInvitation = async (candidateId: string, candidateName: string) => {
    if (!courseId) return;

    const confirmed = await confirm({
      title: 'Send invitasjon',
      message: `Send invitasjon til ${candidateName}?`,
      confirmLabel: 'Send',
      cancelLabel: 'Avbryt'
    });

    if (!confirmed) return;

    try {
      setSendingIndividualInvite(candidateId);
      const response = await authenticatedFetch(`/api/exam-invitations/${courseId}/send/${candidateId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      toast.showSuccess(`Invitasjon sendt til ${candidateName}.`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved sending: ${err.message}`);
    } finally {
      setSendingIndividualInvite(null);
    }
  };

  const handleExcludeCandidate = async () => {
    if (!courseId || !candidateToExclude) return;

    try {
      setExcludingCandidate(candidateToExclude.id);
      const response = await authenticatedFetch(`/api/exam-invitations/${courseId}/exclude/${candidateToExclude.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: excludeReason || 'Møtte ikke opp',
          user_id: currentUser?.id
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to exclude candidate');
      }

      toast.showSuccess(data.message);
      setShowExcludeModal(false);
      setCandidateToExclude(null);
      setExcludeReason('');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil: ${err.message}`);
    } finally {
      setExcludingCandidate(null);
    }
  };

  const handleIncludeCandidate = async (candidateId: string, candidateName: string) => {
    if (!courseId) return;

    const confirmed = await confirm({
      title: 'Inkluder kandidat',
      message: `Inkluder ${candidateName} igjen? Kandidaten vil få ny invitasjonslenke.`,
      confirmLabel: 'Inkluder',
      cancelLabel: 'Avbryt'
    });

    if (!confirmed) return;

    try {
      setExcludingCandidate(candidateId);
      const response = await authenticatedFetch(`/api/exam-invitations/${courseId}/include/${candidateId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to include candidate');
      }

      toast.showSuccess(data.message);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil: ${err.message}`);
    } finally {
      setExcludingCandidate(null);
    }
  };

  const handleDeleteCandidate = async (candidateId: string, candidateName: string) => {
    const confirmed = await confirm({
      title: 'Slett kandidat',
      message: `Er du sikker på at du vil slette kandidaten "${candidateName}"?`,
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/${candidateId}`,
        { method: 'DELETE' }
      );

      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        data = text ? JSON.parse(text) : { success: response.ok };
      } else {
        data = { success: response.ok };
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}: Kunne ikke slette kandidat`);
      }

      toast.showSuccess('Kandidat slettet');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved sletting: ${err.message}`);
    }
  };

  const copyExamLink = (candidateCodeOrUrl: string) => {
    // If input is already a full URL, use it directly; otherwise construct URL
    const url = candidateCodeOrUrl.startsWith('http')
      ? candidateCodeOrUrl
      : `${window.location.origin}/start-exam/${candidateCodeOrUrl}`;
    navigator.clipboard.writeText(url);
    toast.showSuccess('Prøvelenke kopiert til utklippstavlen!');
  };

  const copyInvitationLink = (invitationToken: string) => {
    const url = `${window.location.origin}/exam/invite/${invitationToken}`;
    navigator.clipboard.writeText(url);
    toast.showSuccess('Invitasjonslenke kopiert til utklippstavlen!');
  };

  const handleResetClick = (candidate: CourseCandidate) => {
    setCandidateToReset(candidate);
    setShowResetConfirmModal(true);
  };

  const handleResetConfirm = () => {
    setShowResetConfirmModal(false);
    setShowResetFinalConfirmModal(true);
  };

  const handleResetFinalConfirm = async () => {
    if (!candidateToReset || !candidateToReset.exam_session_id) return;
    if (resetReason.trim().length < 30) {
      toast.showError('Begrunnelse må være minst 30 tegn.');
      return;
    }

    try {
      setResettingSession(true);
      const response = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/${candidateToReset.id}/reset-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: resetReason.trim() })
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || data.error || 'Kunne ikke nullstille besvarelse');
      }

      toast.showSuccess('Besvarelse nullstilt. Kandidaten har fått varsel på e-post og SMS.');
      setShowResetFinalConfirmModal(false);
      setCandidateToReset(null);
      setResetReason('');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved nullstilling: ${err.message}`);
    } finally {
      setResettingSession(false);
    }
  };

  const handleResetCancel = () => {
    setShowResetConfirmModal(false);
    setShowResetFinalConfirmModal(false);
    setCandidateToReset(null);
    setResetReason('');
  };

  // v6.3.4: Reset skal KUN være tilgjengelig FØR sensur (backend håndhever samme regel).
  // Sensurert prøve krever revokering-flyt (systemadmin-only) — dette hindrer
  // utilsiktet ødeleggelse av sensur-arbeid.
  const canResetCandidate = (candidate: CourseCandidate) => {
    return canManageCandidates &&
           candidate.exam_session_id &&
           ['exam_started', 'exam_completed'].includes(candidate.status);
  };

  // v6.3.4: Kopier-prøvelenke er meningsløs etter innlevering — kandidat kan ikke
  // ta prøven igjen. Skjul knappen for alle statuser fra og med exam_completed.
  // v6.6.1 (2026-09-24): Utvidet med certificate_issued + claimed — etter attest
  // er utstedt/hentet er invitasjonslenke helt uaktuelt. Skjules også hvis
  // exam_window_end er passert (backend håndhever tidsvinduet uansett).
  const canCopyInvitationLink = (candidate: CourseCandidate) => {
    if (['exam_completed', 'graded', 'certificate_issued', 'claimed', 'no_show_pending_reschedule', 'self_cancelled'].includes(candidate.status)) return false;
    if (isExamWindowClosed()) return false;
    return true;
  };

  // v6.6.1: Fellesnevner for "kandidaten er ferdig med prøvefasen" — brukes for
  // å skjule invitere/ekskluder/tilrettelegging etter at prøven er innlevert eller
  // attest utstedt/hentet.
  const isCandidateAfterExam = (candidate: CourseCandidate) => {
    return ['exam_completed', 'graded', 'certificate_issued', 'claimed'].includes(candidate.status);
  };

  // v6.6.1: Eksamens-vindu passert? Backend blokkerer submit + start-eksamen etter
  // exam_window_end, så invitasjons-lenker som sendes ut etterpå returnerer feil
  // ("Prøven er stengt") og forvirrer kandidaten. Brukes til å skjule invitasjon-/
  // kopier-lenke-handlinger. IKKE til å skjule overføring-til-sensor (som er hele
  // poenget for no-shows etter passert vindu).
  const isExamWindowClosed = () => {
    const end = (course as any)?.exam_window_end;
    if (!end) return false;
    return new Date(end).getTime() < Date.now();
  };

  // Check if candidate can be deleted
  const canDeleteCandidate = (candidate: CourseCandidate) => {
    return canManageCandidates && !candidate.exam_session_id;
  };

  // Overfør til sensor for manuell vurdering:
  // - Kun for systemadmin / admin (forbund) / medlem-admin (egen org). Vanlig medlem uten admin-flagg blokkeres.
  // - Kun for kandidater UTEN aktiv exam_session (no-show / ikke startet).
  // - Ikke etter sensur eller utstedt attest.
  const canTransferForManualGrading = (candidate: CourseCandidate) => {
    if (!currentUser) return false;
    const isPrivilegedRole =
      currentUser.role === 'systemadmin' ||
      currentUser.role === 'admin' ||
      (currentUser.role === 'member' && (currentUser as any).is_member_admin === true);
    if (!isPrivilegedRole) return false;
    if (candidate.excluded) return false;
    if (['graded', 'exam_completed'].includes(candidate.status)) return false;
    if (candidate.exam_session_id) return false;
    return true;
  };

  const handleTransferClick = (candidate: CourseCandidate) => {
    setCandidateToTransfer(candidate);
    setTransferReason('');
    setShowTransferModal(true);
  };

  const handleTransferCancel = () => {
    setShowTransferModal(false);
    setCandidateToTransfer(null);
    setTransferReason('');
  };

  const handleTransferConfirm = async () => {
    if (!candidateToTransfer || transferReason.trim().length < 30) return;
    setTransferring(true);
    try {
      const response = await authenticatedFetch(
        `/api/admin/course-instances/${courseId}/candidates/${candidateToTransfer.id}/transfer-for-manual-grading`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: transferReason.trim() })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        alert(`Kunne ikke overføre kandidaten: ${data.message || data.error || 'Ukjent feil'}`);
        setTransferring(false);
        return;
      }
      setShowTransferModal(false);
      setCandidateToTransfer(null);
      setTransferReason('');
      alert('Kandidaten er overført til sensor. Sensor får varsel på e-post og kan nå registrere alternativ vurdering.');
      await loadData();
    } catch (err) {
      alert('Uventet feil ved overføring. Prøv igjen.');
      console.error('[transfer-for-manual-grading]', err);
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B5651D] mx-auto mb-4"></div>
          <p className="text-gray-600">Laster kursdetaljer...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Kurs ikke funnet'}
        </div>
        <button
          onClick={() => navigate('/course-instances')}
          className="mt-4 flex items-center text-[#B5651D] hover:text-[#8B4513]"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Tilbake til kurs
        </button>
      </div>
    );
  }

  // Statistics based on candidates
  const registeredCount = candidates.filter(c => c.status === 'registered').length;
  const examStartedCount = candidates.filter(c => c.status === 'exam_started').length;
  const claimedCount = candidates.filter(c => c.status === 'claimed' || c.status === 'certificate_issued').length;

  // Sessions-based statistics (for grading details)
  const passedCount = sessions.filter(s => s.passed === true).length;
  const failedCount = sessions.filter(s => s.passed === false).length;

  // Access control: Only member, admin, and systemadmin can manage sensors
  const canManageSensors = currentUser?.role === 'member' ||
                          currentUser?.role === 'admin' ||
                          currentUser?.role === 'systemadmin';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/course-instances')}
          className="flex items-center text-[#B5651D] hover:text-[#8B4513] mb-4 transition"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Tilbake til kurs
        </button>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{course.name}</h1>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span>Prøvesett: {course.exam_title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>Organisasjon: {course.organization_name}</span>
                </div>
                {course.start_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(course.start_date).toLocaleDateString('nb-NO')}
                      {course.end_date && ` - ${new Date(course.end_date).toLocaleDateString('nb-NO')}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                course.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {course.status === 'active' ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs — horizontal scroll på mobil */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="-mb-px flex gap-4 sm:gap-8 whitespace-nowrap">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
              activeTab === 'overview'
                ? 'border-[#B5651D] text-[#B5651D]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Oversikt
          </button>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
              activeTab === 'candidates'
                ? 'border-[#B5651D] text-[#B5651D]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Kandidater ({candidates.filter(c => !c.excluded && c.status !== 'self_cancelled').length})
          </button>
          <button
            onClick={() => setActiveTab('exam')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
              activeTab === 'exam'
                ? 'border-[#B5651D] text-[#B5651D]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Prøve
          </button>
          <button
            onClick={() => setActiveTab('sensors')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
              activeTab === 'sensors'
                ? 'border-[#B5651D] text-[#B5651D]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Sensorer ({courseSensors.length})
          </button>
          {compositePlanInfo?.is_composite && (
            <button
              onClick={() => setActiveTab('composite')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
                activeTab === 'composite'
                  ? 'border-[#B5651D] text-[#B5651D]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sensur-oversikt ({compositePlanInfo.total_components})
            </button>
          )}
          {/* v6.6.1: «Vurderingsoppsett»-tab gates også på compositePlanInfo — hvis backend
              ikke har assessment_plans-migrasjonene (dvs. prod før bunt-P2), er compositePlanInfo
              null og tab-en skjules. I test-miljø der Fase 1 er live, vises tab-en for alle kurs. */}
          {compositePlanInfo !== null && (
            <button
              onClick={() => setActiveTab('plan')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition flex-shrink-0 ${
                activeTab === 'plan'
                  ? 'border-[#B5651D] text-[#B5651D]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vurderingsoppsett
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Totalt Kandidater</p>
                  <p className="text-2xl font-bold text-gray-900">{candidates.length}</p>
                </div>
                <Users className="w-8 h-8 text-[#B5651D]" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Venter på prøve</p>
                  <p className="text-2xl font-bold text-gray-600">{registeredCount}</p>
                </div>
                <Clock className="w-8 h-8 text-gray-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Prøve pågår</p>
                  <p className="text-2xl font-bold text-yellow-600">{examStartedCount}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bestått</p>
                  <p className="text-2xl font-bold text-green-600">{passedCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ikke Bestått</p>
                  <p className="text-2xl font-bold text-red-600">{failedCount}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Attest hentet</p>
                  <p className="text-2xl font-bold text-purple-600">{claimedCount}</p>
                </div>
                <GraduationCap className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Course Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Kursinformasjon</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Prøvesett</dt>
                <dd className="mt-1 text-sm text-gray-900">{course.exam_title}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Studieplan</dt>
                <dd className="mt-1 text-sm text-gray-900">{course.study_plan_title || 'N/A'}</dd>
              </div>
              {Array.isArray(course.included_modules) && course.included_modules.length > 0 && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">
                    Moduldekning ({course.exam_set_included_module_ids ? 'Subset' : 'Hele studieplanen'})
                  </dt>
                  <dd className="mt-1">
                    <ul className="space-y-1 text-sm">
                      {course.included_modules.map((mod) => (
                        <li key={mod.id} className="flex items-start gap-2">
                          <span className="text-xs font-mono bg-kf-blue-50 text-kf-blue-800 px-1.5 py-0.5 rounded">
                            {mod.code}
                          </span>
                          <span className="flex-1">
                            <span className="font-medium">{mod.title}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              {mod.ects_credits ? `${mod.ects_credits} sp` : ''}
                              {mod.estimated_hours ? ` · ${mod.estimated_hours}t` : ''}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                    {course.exam_set_included_module_ids && (
                      <p className="text-xs text-gray-500 mt-2">
                        Kompetanseattest for kandidater som består dette kurset
                        vil reflektere kun læringsutbyttene i disse modulene.
                      </p>
                    )}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Maks kandidater</dt>
                <dd className="mt-1 text-sm text-gray-900">{course.max_candidates || 'Ubegrenset'}</dd>
              </div>
              {/* v6.6.2: API Aktivert / API Lenke fjernet */}
            </dl>
          </div>
        </div>
      )}

      {activeTab === 'candidates' && (
        <div className="space-y-4">
          {/* Filter-banner fra ?filter=awaiting_sensur — brukt når bruker kommer
              hit via "Åpne kurs" fra Til-sensur-lista. Lar bruker fjerne filter
              uten å navigere bort. */}
          {candidateFilter === 'awaiting_sensur' && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <AlertCircle className="h-4 w-4" />
                <span>
                  Viser kun kandidater som venter sensur (status: innlevert, ikke ferdig sensurert).
                </span>
              </div>
              <button
                onClick={() => {
                  setCandidateFilter(null);
                  const next = new URLSearchParams(searchParams);
                  next.delete('filter');
                  setSearchParams(next, { replace: true });
                }}
                className="text-xs font-medium text-amber-900 underline hover:no-underline"
              >
                Vis alle kandidater
              </button>
            </div>
          )}

          {/* v6.6.1: Pending-godkjenninger-panel (vises kun ved manual/hybrid + faktiske pendings) */}
          {course && courseId && (
            <CandidateApprovalsPanel
              instanceId={courseId}
              approvalMode={(course.approval_mode as 'auto' | 'hybrid' | 'manual') || 'auto'}
              maxCandidates={course.max_candidates}
              onChange={() => { loadData(); }}
            />
          )}

          {/* Add Candidate Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-1">
              Påmeldte kandidater
              <HelpTooltip slug="exam-session-vs-course-candidate" size="sm" />
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowBroadcastModal(true)}
                disabled={candidates.filter(c => !c.excluded).length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="Send SMS/e-post til kandidater (course_start_info, exam_reminder, custom)"
              >
                <MessageSquare size={16} />
                Send melding
                <HelpTooltip slug="broadcast-templates" size="sm" />
              </button>
              <button
                onClick={() => setShowCsvImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-[#B5651D] text-[#B5651D] rounded-lg hover:bg-[#B5651D] hover:text-white transition whitespace-nowrap"
              >
                <Upload size={16} />
                CSV-import
              </button>
              <button
                onClick={() => setShowAddCandidateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#B5651D] text-white rounded-lg hover:bg-[#8B4513] transition whitespace-nowrap"
              >
                <Plus size={16} />
                Registrer kandidat
              </button>
            </div>
          </div>

          {/* v6.6.1: Info-boks når eksamensvinduet er passert */}
          {isExamWindowClosed() && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Eksamensvinduet er passert</p>
                <p>
                  «Send invitasjon» og «Kopier invitasjonslenke» er skjult fordi lenken vil
                  gi «Prøven er stengt» hvis kandidaten klikker på den. Kandidater som ikke
                  møtte og likevel skal vurderes (basert på prosjekt/muntlig levert utenfor
                  systemet) kan overføres til sensor for manuell vurdering via
                  <UserCheck className="inline h-4 w-4 mx-1 text-indigo-600" />
                  -ikonet på raden.
                </p>
              </div>
            </div>
          )}

          {/* Invitation Status Summary */}
          {candidates.length > 0 && (
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                <div>
                  <div className="text-kf-blue-600 font-medium">{candidates.filter(c => !c.excluded).length}</div>
                  <div className="text-kf-blue-500">Aktive</div>
                </div>
                <div>
                  <div className="text-green-600 font-medium">{candidates.filter(c => c.invitation_sent_at && !c.excluded).length}</div>
                  <div className="text-green-500">Invitert</div>
                </div>
                <div>
                  <div className="text-yellow-600 font-medium">{candidates.filter(c => c.status === 'exam_started').length}</div>
                  <div className="text-yellow-500">Startet</div>
                </div>
                <div>
                  <div className="text-indigo-600 font-medium">{candidates.filter(c => c.status === 'exam_completed' || c.status === 'graded').length}</div>
                  <div className="text-indigo-500">Fullført</div>
                </div>
                <div>
                  <div className="text-red-600 font-medium">{candidates.filter(c => c.excluded).length}</div>
                  <div className="text-red-500">Ekskludert</div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Navn
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kandidatkode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invitasjon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    // Grunnfilter: skjul venteliste (eksisterende adferd).
                    // Deretter: ved aktivt awaiting_sensur-filter vises bare kandidater
                    // som har levert men mangler endelig sensur (status=exam_completed).
                    const base = candidates.filter(c => c.status !== 'waitlist');
                    const filtered = candidateFilter === 'awaiting_sensur'
                      ? base.filter(c => c.status === 'exam_completed')
                      : base;
                    if (filtered.length === 0) {
                      const emptyMsg = candidateFilter === 'awaiting_sensur'
                        ? 'Ingen kandidater som venter sensur akkurat nå.'
                        : 'Ingen påmeldte kandidater ennå. Bruk knappene over for å registrere kandidater.';
                      return (
                        <tr>
                          <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                            {emptyMsg}
                          </td>
                        </tr>
                      );
                    }
                    return filtered.map((candidate) => {
                      // Find matching exam session if exists
                      const examSession = sessions.find(s =>
                        s.candidate_code === candidate.candidate_code ||
                        s.id === candidate.exam_session_id
                      );

                      const getStatusInfo = () => {
                        if (candidate.status === 'certificate_issued') {
                          return { text: 'Attest utstedt', color: 'bg-purple-100 text-purple-800', icon: '🎓' };
                        }
                        if (candidate.status === 'claimed') {
                          return { text: 'Hentet', color: 'bg-green-100 text-green-800', icon: '✅' };
                        }
                        if (candidate.status === 'graded') {
                          return { text: 'Sensurert', color: 'bg-kf-blue-100 text-kf-blue-800', icon: '📝' };
                        }
                        if (candidate.status === 'exam_completed') {
                          return { text: 'Prøve fullført', color: 'bg-indigo-100 text-indigo-800', icon: '📋' };
                        }
                        if (candidate.status === 'exam_started') {
                          return { text: 'Prøve startet', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' };
                        }
                        return { text: 'Registrert', color: 'bg-gray-100 text-gray-800', icon: '📌' };
                      };

                      const statusInfo = getStatusInfo();

                      // Get invitation status
                      const getInvitationStatus = () => {
                        if (candidate.excluded) {
                          return { text: 'Ekskludert', color: 'bg-red-100 text-red-800', reason: candidate.excluded_reason };
                        }
                        if (candidate.invitation_sent_at) {
                          return {
                            text: 'Sendt',
                            color: 'bg-green-100 text-green-800',
                            sentAt: candidate.invitation_sent_at,
                            emailSent: candidate.invitation_email_sent,
                            smsSent: candidate.invitation_sms_sent
                          };
                        }
                        return { text: 'Venter', color: 'bg-gray-100 text-gray-600' };
                      };

                      const inviteStatus = getInvitationStatus();

                      return (
                        <React.Fragment key={candidate.id}>
                        <tr className={`hover:bg-gray-50 ${candidate.excluded ? 'opacity-60' : ''}`}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{candidate.full_name}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone size={12} />
                              {candidate.mobile_number}
                            </div>
                            {candidate.email && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Mail size={12} />
                                {candidate.email}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-mono text-sm font-medium text-gray-900">
                              {candidate.candidate_code}
                            </div>
                            {examSession && examSession.percentage !== null && (
                              <div className="text-xs text-gray-500 mt-1">
                                Resultat: {examSession.percentage}%
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded inline-flex items-center gap-1 ${inviteStatus.color}`}>
                              {candidate.excluded ? <Ban size={12} /> : inviteStatus.text === 'Sendt' ? <CheckCircle size={12} /> : <Clock size={12} />}
                              {inviteStatus.text}
                            </span>
                            {inviteStatus.reason && (
                              <div className="text-xs text-red-600 mt-1">{inviteStatus.reason}</div>
                            )}
                            {inviteStatus.sentAt && (
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(inviteStatus.sentAt).toLocaleDateString('nb-NO')}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {canResetCandidate(candidate) ? (
                              <button
                                onClick={() => handleResetClick(candidate)}
                                className={`px-2 py-1 text-xs font-medium rounded inline-flex items-center gap-1 ${statusInfo.color} hover:opacity-80 cursor-pointer transition`}
                                title="Klikk for å nullstille besvarelse"
                              >
                                <span>{statusInfo.icon}</span>
                                {statusInfo.text}
                              </button>
                            ) : (
                              <span className={`px-2 py-1 text-xs font-medium rounded inline-flex items-center gap-1 ${statusInfo.color}`}>
                                <span>{statusInfo.icon}</span>
                                {statusInfo.text}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {candidate.graded_at ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <span>{new Date(candidate.graded_at).toLocaleDateString('nb-NO')}</span>
                                  <button
                                    onClick={() => setExpandedSensurCandidate(
                                      expandedSensurCandidate === candidate.id ? null : candidate.id
                                    )}
                                    className="text-kf-blue-600 hover:text-kf-blue-800 text-xs underline"
                                    title="Vis/skjul sensur-detaljer"
                                  >
                                    {expandedSensurCandidate === candidate.id ? 'Skjul' : 'Vis'}
                                  </button>
                                </div>
                                <div className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
                                  <span>Sensurert</span>
                                  {candidate.grade && <span>· {candidate.grade}</span>}
                                  {candidate.alternative_assessment && (() => {
                                    const label = parseAssessmentFormShort(candidate.alternative_assessment_note);
                                    const colorClass = getAssessmentFormBadgeClass(candidate.alternative_assessment_note);
                                    return label ? (
                                      <span
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${colorClass}`}
                                        title="Vurderingsform benyttet ved sensurering"
                                      >
                                        {label}
                                      </span>
                                    ) : null;
                                  })()}
                                </div>
                              </>
                            ) : candidate.exam_submitted_at ? (
                              <>
                                <div>{new Date(candidate.exam_submitted_at).toLocaleDateString('nb-NO')}</div>
                                <div className="text-xs text-gray-400">Levert</div>
                              </>
                            ) : candidate.exam_started_at ? (
                              <>
                                <div>{new Date(candidate.exam_started_at).toLocaleDateString('nb-NO')}</div>
                                <div className="text-xs text-gray-400">Startet prøve</div>
                              </>
                            ) : candidate.created_at ? (
                              <>
                                <div>{new Date(candidate.created_at).toLocaleDateString('nb-NO')}</div>
                                <div className="text-xs text-gray-400">
                                  {candidate.imported_via === 'csv' ? 'CSV-import' : 'Manuell'}
                                </div>
                              </>
                            ) : (
                              <div className="text-gray-400">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(candidate.candidate_code);
                                  toast.showSuccess('Kandidatkode kopiert!');
                                }}
                                className="text-[#B5651D] hover:text-[#8B4513] transition"
                                title="Kopier kandidatkode"
                              >
                                <Copy size={16} />
                              </button>
                              {/* Copy invitation link — kun tilgjengelig FØR innlevering
                                  (v6.3.4: skjules etter exam_completed/graded siden kandidat
                                  ikke kan ta prøven igjen). */}
                              {candidate.invitation_token && canCopyInvitationLink(candidate) && (
                                <button
                                  onClick={() => copyInvitationLink(candidate.invitation_token!)}
                                  className="text-kf-blue-600 hover:text-kf-blue-800 transition"
                                  title="Kopier invitasjonslenke"
                                >
                                  <Link size={16} />
                                </button>
                              )}
                              {/* Send invite / Re-send invite — kun før prøven er innlevert
                                  OG før eksamensvinduet er passert. Etter exam_window_end vil
                                  invitasjonslenken uansett gi "Prøven er stengt" i backend. */}
                              {!candidate.excluded && candidate.status !== 'exam_started' && !isCandidateAfterExam(candidate) && !isExamWindowClosed() && (
                                <button
                                  onClick={() => handleSendIndividualInvitation(candidate.id, candidate.full_name)}
                                  disabled={sendingIndividualInvite === candidate.id}
                                  className={`${candidate.invitation_sent_at ? 'text-amber-600 hover:text-amber-800' : 'text-green-600 hover:text-green-800'} transition disabled:opacity-50`}
                                  title={candidate.invitation_sent_at ? 'Send invitasjon på nytt' : 'Send invitasjon'}
                                >
                                  <Send size={16} />
                                </button>
                              )}
                              {/* Exclude/Include buttons — kun før prøven er innlevert. */}
                              {!candidate.excluded && !isCandidateAfterExam(candidate) && (
                                <button
                                  onClick={() => {
                                    setCandidateToExclude(candidate);
                                    setShowExcludeModal(true);
                                  }}
                                  className="text-red-500 hover:text-red-700 transition"
                                  title="Ekskluder kandidat"
                                >
                                  <UserMinus size={16} />
                                </button>
                              )}
                              {candidate.excluded && (
                                <button
                                  onClick={() => handleIncludeCandidate(candidate.id, candidate.full_name)}
                                  disabled={excludingCandidate === candidate.id}
                                  className="text-green-600 hover:text-green-800 transition disabled:opacity-50"
                                  title="Inkluder kandidat igjen"
                                >
                                  <UserPlus size={16} />
                                </button>
                              )}
                              {/* Tilrettelegging (v6.2.9) — vises for alle ikke-ekskluderte kandidater
                                  som ikke har levert eller mottatt attest. Kan settes FØR eksamen startes
                                  (via candidateId) eller ETTER (via sessionId). Ved sesjons-opprettelse
                                  overføres pre-invitation-tilrettelegging automatisk til sesjonen.
                                  v6.6.1 (2026-09-24): utvidet til å skjule etter exam_completed også —
                                  tilrettelegging etter innlevering er meningsløst. */}
                              {!candidate.excluded && !isCandidateAfterExam(candidate) && (
                                <button
                                  onClick={() => setAccommodationCandidate({
                                    sessionId: candidate.exam_session_id || undefined,
                                    candidateId: candidate.exam_session_id ? undefined : candidate.id,
                                    name: candidate.full_name
                                  })}
                                  className="text-purple-600 hover:text-purple-800 transition"
                                  title={candidate.exam_session_id
                                    ? "Sett tilrettelegging (utvidet tid, muntlig prøve, etc.)"
                                    : "Sett tilrettelegging FØR invitasjon sendes (utvidet tid, muntlig prøve, etc.) — gjelder fra første minutt når kandidaten starter"}
                                >
                                  <Accessibility size={16} />
                                </button>
                              )}
                              {canResetCandidate(candidate) && (
                                <button
                                  onClick={() => handleResetClick(candidate)}
                                  className="text-amber-600 hover:text-amber-800 transition"
                                  title="Nullstill besvarelse"
                                >
                                  <RotateCcw size={16} />
                                </button>
                              )}
                              {canDeleteCandidate(candidate) && !candidate.excluded && (
                                <button
                                  onClick={() => handleDeleteCandidate(candidate.id, candidate.full_name)}
                                  className="text-red-600 hover:text-red-800 transition"
                                  title="Slett kandidat"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                              {canTransferForManualGrading(candidate) && (
                                <button
                                  onClick={() => handleTransferClick(candidate)}
                                  className="text-indigo-600 hover:text-indigo-800 transition"
                                  title="Overfør til sensor for manuell vurdering (no-show + prosjekt/muntlig levert)"
                                >
                                  <UserCheck size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* v6.3.5: Ekspandert sensur-detaljer for gradert kandidat */}
                        {expandedSensurCandidate === candidate.id && candidate.graded_at && (
                          <tr>
                            <td colSpan={99} className="px-6 py-4 bg-gray-50 border-t border-b border-gray-200">
                              <div className={`rounded-lg p-4 ${candidate.alternative_assessment ? 'bg-purple-50 border border-purple-200' : 'bg-kf-blue-50 border border-kf-blue-200'}`}>
                                <div className="flex items-start justify-between mb-3">
                                  <h4 className={`font-semibold ${candidate.alternative_assessment ? 'text-purple-900' : 'text-kf-blue-900'}`}>
                                    {candidate.alternative_assessment ? '⚠️ Sensurert med alternativ vurdering' : '📝 Sensur-detaljer'}
                                  </h4>
                                  <button
                                    onClick={() => setExpandedSensurCandidate(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                    aria-label="Lukk"
                                  >
                                    <XCircle size={18} />
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                  <div><strong>Karakter:</strong> <span className={candidate.passed ? 'text-green-700' : 'text-red-700'}>{candidate.grade || '(ikke satt)'}</span></div>
                                  <div><strong>Resultat:</strong> {candidate.passed ? '✅ Bestått' : '❌ Ikke bestått'}</div>
                                  {candidate.percentage !== null && candidate.percentage !== undefined && (
                                    <div><strong>Prosentscore:</strong> {candidate.percentage}%</div>
                                  )}
                                  {candidate.sensor_name && (
                                    <div><strong>Sensor:</strong> {candidate.sensor_name}</div>
                                  )}
                                  {candidate.sensored_at && (
                                    <div><strong>Sensurert:</strong> {new Date(candidate.sensored_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}</div>
                                  )}
                                </div>
                                {candidate.alternative_assessment && candidate.alternative_assessment_note && (
                                  <div className="mt-3 pt-3 border-t border-purple-200">
                                    <strong className="text-sm text-purple-900">Begrunnelse for alternativ vurdering:</strong>
                                    <pre className="mt-1 text-xs text-gray-800 bg-white p-3 rounded border border-purple-100 whitespace-pre-wrap font-sans">
{candidate.alternative_assessment_note}
                                    </pre>
                                  </div>
                                )}
                                {candidate.sensor_comment && (
                                  <div className="mt-3 pt-3 border-t border-kf-blue-200">
                                    <strong className="text-sm text-kf-blue-900">Sensor-kommentar:</strong>
                                    <p className="mt-1 text-sm text-gray-800 bg-white p-3 rounded border border-kf-blue-100 whitespace-pre-wrap">
                                      {candidate.sensor_comment}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Venteliste-seksjon */}
          {candidates.filter(c => c.status === 'waitlist').length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-700" />
                <h4 className="text-sm font-semibold text-amber-900">
                  Venteliste ({candidates.filter(c => c.status === 'waitlist').length})
                </h4>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Posisjon</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Navn</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kontakt</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Påmeldt</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Handling</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {candidates.filter(c => c.status === 'waitlist').map(wl => (
                    <tr key={wl.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm text-amber-900 font-medium">
                        #{wl.waitlist_position ?? '?'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{wl.full_name}</div>
                        <div className="text-xs text-gray-500">Kode: {wl.candidate_code}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        {wl.email && <div className="flex items-center gap-1"><Mail size={11} /> {wl.email}</div>}
                        <div className="flex items-center gap-1"><Phone size={11} /> {wl.mobile_number}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(wl.created_at).toLocaleDateString('nb-NO')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handlePromoteFromWaitlist(wl.id)}
                          disabled={promotingCandidate === wl.id}
                          className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1 disabled:opacity-50"
                          title="Flytt opp til påmeldt"
                        >
                          <ArrowUpCircle size={12} />
                          {promotingCandidate === wl.id ? 'Promoterer…' : 'Promoter'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'exam' && (() => {
        const eligible = candidates.filter(c =>
          !c.excluded &&
          (c.status === 'registered' || c.status === 'invited')
        );
        const attended = eligible.filter(c => c.attendance_marked === true);
        const notMarked = eligible.filter(c => c.attendance_marked !== true);
        const examInProgress = candidates.filter(c => c.status === 'exam_started');
        const examFinished = candidates.filter(c =>
          ['exam_completed', 'graded', 'certificate_issued', 'claimed'].includes(c.status)
        );
        const alreadyStarted = [...examInProgress, ...examFinished];
        const allMarkedIds = eligible.filter(c => c.attendance_marked === true).map(c => c.id);
        const allNotMarkedIds = eligible.filter(c => c.attendance_marked !== true).map(c => c.id);

        return (
          <div className="space-y-6">
            {/* Hjelpetekst */}
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 text-sm text-kf-blue-900">
              <p className="font-semibold mb-1">Slik fungerer prøvegjennomføringen</p>
              <ol className="list-decimal list-inside space-y-1 text-kf-blue-800">
                <li>Hak av oppmøte for kandidatene som faktisk har dukket opp</li>
                <li>Klikk <strong>Start eksamen for fremmøtte</strong> — vi sender SEB-eksamenslenke til de fremmøtte</li>
                <li>Ikke-fremmøtte kandidater får status "ikke møtt" og kan flyttes til ny instans senere</li>
              </ol>
            </div>

            {/* Status-bånd */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <p className="text-sm text-gray-600">Kandidater totalt</p>
                <p className="text-2xl font-bold text-gray-900">{eligible.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-green-200">
                <p className="text-sm text-green-700">Fremmøtt</p>
                <p className="text-2xl font-bold text-green-700">{attended.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-amber-200">
                <p className="text-sm text-amber-700">Ikke markert</p>
                <p className="text-2xl font-bold text-amber-700">{notMarked.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-yellow-200">
                <p className="text-sm text-yellow-700">Prøve påbegynt</p>
                <p className="text-2xl font-bold text-yellow-700">{examInProgress.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-indigo-200">
                <p className="text-sm text-indigo-700">Prøve fullført</p>
                <p className="text-2xl font-bold text-indigo-700">{examFinished.length}</p>
              </div>
            </div>

            {/* Task #139: SEB-test-status — vises når exam_set krever SEB. Lister
                hvem som har/ikke har fullført SEB-testen før eksamen. */}
            {eligible.some(c => c.seb_test_invited || c.seb_test_completed_at) && (() => {
              const sebCompleted = eligible.filter(c => c.seb_test_completed_at);
              const sebInvitedNotCompleted = eligible.filter(c => c.seb_test_invited && !c.seb_test_completed_at);
              const sebNotInvited = eligible.filter(c => !c.seb_test_invited && !c.seb_test_completed_at);
              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    SEB-test-status
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Kandidater må gjennomføre SEB-testen før de kan starte eksamen. Automatisk purring sendes 24t og 2t før eksamen hvis ikke fullført.
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-700">Fullført</p>
                      <p className="text-2xl font-bold text-green-700">{sebCompleted.length}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded p-3">
                      <p className="text-amber-700">Invitert, ikke fullført</p>
                      <p className="text-2xl font-bold text-amber-700">{sebInvitedNotCompleted.length}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-red-700">Ikke invitert</p>
                      <p className="text-2xl font-bold text-red-700">{sebNotInvited.length}</p>
                    </div>
                  </div>
                  {sebInvitedNotCompleted.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-amber-700 font-medium">
                        Vis {sebInvitedNotCompleted.length} kandidat{sebInvitedNotCompleted.length === 1 ? '' : 'er'} som ikke har fullført
                      </summary>
                      <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-800">
                        {sebInvitedNotCompleted.map(c => (
                          <li key={c.id}>
                            • {c.full_name} <span className="text-gray-500">({c.email || c.mobile_number || 'ingen kontakt'})</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })()}

            {/* Task #134: Post-eksamen samleoversikt — vises når eksamensvindu er lukket
                OG det finnes minst én gjennomført besvarelse. Gir admin én blokk med
                status-sammendrag på tvers av hele kurs-syklusen. */}
            {(() => {
              const windowEnd = (course as any)?.exam_window_end;
              const windowClosed = windowEnd && new Date(windowEnd) < new Date();
              if (!windowClosed || examFinished.length === 0) return null;
              const pendingGrading = candidates.filter(c => c.status === 'exam_completed').length;
              const graded = candidates.filter(c => c.status === 'graded').length;
              const certIssued = candidates.filter(c => c.status === 'certificate_issued').length;
              const claimed = candidates.filter(c => c.status === 'claimed').length;
              const noShow = eligible.filter(c => !c.attendance_marked).length;
              return (
                <div className="bg-slate-50 rounded-lg shadow-sm border border-slate-200 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Samleoversikt etter eksamen
                  </h3>
                  <p className="text-xs text-gray-600 mb-4">
                    Eksamensvinduet ble lukket {new Date(windowEnd).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' })}. Her er status på hele kurs-syklusen.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-white rounded border border-slate-200 p-3">
                      <p className="text-gray-600">Påmeldt totalt</p>
                      <p className="text-xl font-bold text-gray-900">{eligible.length}</p>
                    </div>
                    <div className="bg-white rounded border border-amber-200 p-3">
                      <p className="text-amber-700">Ikke møtt</p>
                      <p className="text-xl font-bold text-amber-700">{noShow}</p>
                    </div>
                    <div className="bg-white rounded border border-yellow-200 p-3">
                      <p className="text-yellow-700">Venter på sensur</p>
                      <p className="text-xl font-bold text-yellow-700">{pendingGrading}</p>
                    </div>
                    <div className="bg-white rounded border border-blue-200 p-3">
                      <p className="text-blue-700">Ferdig sensurert</p>
                      <p className="text-xl font-bold text-blue-700">{graded}</p>
                    </div>
                    <div className="bg-white rounded border border-emerald-200 p-3">
                      <p className="text-emerald-700">Attest utstedt</p>
                      <p className="text-xl font-bold text-emerald-700">{certIssued}</p>
                    </div>
                    <div className="bg-white rounded border border-emerald-300 p-3">
                      <p className="text-emerald-800">Attest hentet</p>
                      <p className="text-xl font-bold text-emerald-800">{claimed}</p>
                    </div>
                  </div>
                  {pendingGrading > 0 && (
                    <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                      ⚠️ {pendingGrading} besvarelse{pendingGrading === 1 ? '' : 'r'} venter fortsatt på sensur. Sensor varsles automatisk hver 7. dag.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Hovedhandlinger */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
              {/* Rad 1: Bulk oppmøte */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleBulkAttendance(allNotMarkedIds, true)}
                  disabled={allNotMarkedIds.length === 0}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle size={14} />
                  Hak av alle ({allNotMarkedIds.length})
                </button>
                <button
                  onClick={() => handleBulkAttendance(allMarkedIds, false)}
                  disabled={allMarkedIds.length === 0}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <XCircle size={14} />
                  Fjern alle ({allMarkedIds.length})
                </button>
              </div>

              {/* Rad 2: To handlinger side-om-side med hver sin forklaring */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Send invitasjon på forhånd */}
                <div className="border border-kf-blue-200 bg-kf-blue-50 rounded p-3 flex flex-col">
                  <div className="text-xs text-kf-blue-900 mb-2 flex-1">
                    <strong>Send invitasjon</strong>
                    <p className="mt-1">
                      Sender e-post + SMS til <strong>de {attended.length} valgte kandidatene</strong>
                      {' '}(avkryssingene nedenfor) med informasjon om planlagt eksamens-tidspunkt.
                      <br />Lenken i meldingen er sperret av systemet inntil oppsatt starttidspunkt —
                      kandidatene får melding om at prøven ikke er åpen ennå hvis de klikker for tidlig.
                      <br /><em>Bruk denne f.eks. kvelden før eksamen — hak av alle først.</em>
                    </p>
                  </div>
                  <button
                    onClick={() => handleSendInvitations(attended.map(c => c.id))}
                    disabled={sendingInvitations || attended.length === 0}
                    className="px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700 transition disabled:opacity-50 inline-flex items-center gap-2 font-medium justify-center"
                  >
                    <Send size={16} />
                    {sendingInvitations ? 'Sender…' : `Send invitasjon til ${attended.length} valgte`}
                  </button>
                </div>

                {/* Start eksamen for fremmøtte */}
                <div className="border border-green-200 bg-green-50 rounded p-3 flex flex-col">
                  <div className="text-xs text-green-900 mb-2 flex-1">
                    <strong>Start eksamen for fremmøtte</strong>
                    <p className="mt-1">
                      Aktiverer eksamen umiddelbart. Sender "Eksamen er startet"-melding
                      med SEB-lenke KUN til de {attended.length} som er huket av som fremmøtte.
                      <br />Kandidater som ikke er huket av markeres som "ikke møtt"
                      og kan flyttes til ny instans senere.
                      <br /><em>Bruk denne når kurset faktisk starter — hak av kun de som faktisk er på plass.</em>
                    </p>
                  </div>
                  <button
                    onClick={handleStartExamForAttendees}
                    disabled={startingExam || attended.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 inline-flex items-center gap-2 font-medium justify-center"
                  >
                    <PlayCircle size={18} />
                    {startingExam ? 'Starter…' : `Start eksamen for ${attended.length} fremmøtte`}
                  </button>
                </div>
              </div>
            </div>

            {/* Tabell over kandidater med oppmøte-toggle */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kandidat</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kandidatkode</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Oppmøte</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {eligible.length === 0 && alreadyStarted.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-sm">
                          Ingen kandidater å starte eksamen for. Registrer kandidater først via Kandidater-fanen.
                        </td>
                      </tr>
                    ) : null}
                    {eligible.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{c.full_name}</div>
                          <div className="text-xs text-gray-500">{c.email || c.mobile_number}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-gray-900">{c.candidate_code}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={c.attendance_marked === true}
                              onChange={(e) => handleToggleAttendance(c.id, e.target.checked)}
                              disabled={savingAttendance === c.id}
                              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className={`ml-2 text-xs ${c.attendance_marked === true ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                              {savingAttendance === c.id ? 'Lagrer…' : c.attendance_marked === true ? 'Tilstede' : 'Marker'}
                            </span>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs rounded ${c.status === 'invited' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {alreadyStarted.length > 0 && (
                      <>
                        <tr className="bg-indigo-50">
                          <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-indigo-900 uppercase tracking-wide">
                            Prøve påbegynt / fullført ({alreadyStarted.length})
                          </td>
                        </tr>
                        {alreadyStarted.map(c => (
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">{c.full_name}</div>
                              <div className="text-xs text-gray-500">{c.email || c.mobile_number}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-sm text-gray-900">{c.candidate_code}</span>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-400 text-xs">—</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">
                                {c.status === 'graded' && c.passed ? 'Bestått' : c.status === 'graded' && c.passed === false ? 'Ikke bestått' : c.status}
                              </span>
                              {c.percentage !== null && (
                                <span className="ml-2 text-xs text-gray-500">{c.percentage}%</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'sensors' && (
        <div className="space-y-4">
          {canManageSensors && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowAddSensorModal(true)}
                className="bg-[#B5651D] text-white px-4 py-2 rounded-lg hover:bg-[#8B4513] transition flex items-center gap-2"
              >
                <GraduationCap className="w-5 h-5" />
                Tildel Sensor
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sensor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      E-post
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rolle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tildelt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {courseSensors.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        Ingen sensorer tildelt ennå
                      </td>
                    </tr>
                  ) : (
                    courseSensors.map((sensor) => (
                      <tr key={sensor.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{sensor.sensor_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{sensor.sensor_email}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {sensor.role === 'primary' ? 'Primær' : 'Sekundær'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(sensor.assigned_at).toLocaleDateString('nb-NO')}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {canManageSensors ? (
                            <button
                              onClick={() => handleUnassignSensor(sensor.id, sensor.sensor_name)}
                              className="text-red-600 hover:text-red-800 transition"
                              title="Fjern sensor"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Blokk 5 T1.17 — Sensur-oversikt-matrise (kun kompositt) */}
      {activeTab === 'composite' && courseId && (
        <CompositeAssessmentMatrix
          courseInstanceId={courseId}
          onFinalize={(candidateId) => setFinalizingCandidateId(candidateId)}
        />
      )}

      {/* Fase 2.5 T2.5.1 — Vurderingsoppsett-editor */}
      {activeTab === 'plan' && courseId && (
        <CompositePlanEditor courseInstanceId={courseId} />
      )}

      {/* Fase 2.5 T2.5.2 — Finalize-modal (aktiveres via matrise/kandidat-rad) */}
      {finalizingCandidateId && (
        <FinalizeAssessmentModal
          candidateId={finalizingCandidateId}
          onClose={() => setFinalizingCandidateId(null)}
          onFinalized={() => { setFinalizingCandidateId(null); }}
        />
      )}

      {/* Add Sensor Modal */}
      {showAddSensorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Tildel Sensor til Kurs
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Velg Sensor
                  </label>
                  <select
                    value={selectedSensorId}
                    onChange={(e) => setSelectedSensorId(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  >
                    <option value="">Velg sensor...</option>
                    {availableSensors
                      .filter(s => s.status === 'active')
                      .filter(s => !courseSensors.some(cs => cs.sensor_user_id === s.id))
                      .map(sensor => (
                        <option key={sensor.id} value={sensor.id}>
                          {sensor.full_name} ({sensor.email})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => handleAssignSensor()}
                    disabled={!selectedSensorId || assigning}
                    className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                  >
                    {assigning ? 'Tildeler...' : 'Tildel'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddSensorModal(false);
                      setSelectedSensorId('');
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v6.2.4: Habilitet-bypass-dialog (blokkert eller bekreftelse med evt. begrunnelse) */}
      {habilityDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleHabilityCancel}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className={`h-6 w-6 flex-shrink-0 ${habilityDialog.blocked ? 'text-red-600' : 'text-amber-600'}`} />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {habilityDialog.blocked ? 'Tildeling blokkert' : 'Habilitet-varsel'}
                  </h3>
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{habilityDialog.message}</p>
                </div>
              </div>

              {!habilityDialog.blocked && habilityDialog.requiresReason && (
                <div className="mt-4">
                  {habilityDialog.reasonPrompt && (
                    <p className="text-sm text-gray-700 mb-2">{habilityDialog.reasonPrompt}</p>
                  )}
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Begrunnelse (min. {habilityDialog.reasonMinLength} tegn)
                  </label>
                  <textarea
                    value={habilityReason}
                    onChange={(e) => setHabilityReason(e.target.value)}
                    rows={4}
                    placeholder="F.eks: Sensor er faglig leder for kurset og har relevant fagkompetanse. Ingen andre kvalifiserte sensorer er tilgjengelig i organisasjonen. Habilitet vurdert av leder [navn]."
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-600 focus:ring-amber-600 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {habilityReason.trim().length} / {habilityDialog.reasonMinLength} tegn
                  </p>
                </div>
              )}

              {/* v6.2.4: Faglig-bekreftelses-notis vises alltid (både med og uten begrunnelse-krav) */}
              {!habilityDialog.blocked && habilityDialog.fagligConfirmationNotice && (
                <div className="mt-4 bg-kf-blue-50 border border-kf-blue-200 rounded p-3">
                  <p className="text-sm text-kf-blue-900">
                    <span className="font-medium">Faglig bekreftelse: </span>
                    {habilityDialog.fagligConfirmationNotice}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-6">
                <button
                  onClick={handleHabilityCancel}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded font-medium"
                >
                  {habilityDialog.blocked ? 'Lukk' : 'Avbryt'}
                </button>
                {!habilityDialog.blocked && (
                  <button
                    onClick={handleHabilityConfirm}
                    disabled={assigning || (habilityDialog.requiresReason && habilityReason.trim().length < habilityDialog.reasonMinLength)}
                    className="px-4 py-2 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assigning ? 'Tildeler...' : 'Bekreft og tildel'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidate Modal */}
      {showAddCandidateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {!registeredCandidate ? (
                <>
                  <h2 className="text-xl font-semibold mb-4">Registrer ny kandidat</h2>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium text-amber-900 mb-1">🔐 Identitetsverifisering</h3>
                    <p className="text-sm text-amber-700">
                      Informasjonen brukes til å verifisere kandidatens identitet når de henter kompetanseattest via Vipps.
                      Fødselsdato og mobilnummer må matche eksakt med Vipps-kontoen.
                    </p>
                  </div>

                  <form onSubmit={handleRegisterCandidate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fullt navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={candidateFormData.full_name}
                        onChange={(e) => setCandidateFormData({ ...candidateFormData, full_name: e.target.value })}
                        placeholder="Ola Nordmann"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B5651D] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fødselsdato <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={candidateFormData.date_of_birth}
                        onChange={(e) => setCandidateFormData({ ...candidateFormData, date_of_birth: e.target.value })}
                        required
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B5651D] focus:border-transparent"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Må matche kandidatens Vipps-konto eksakt
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mobilnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={candidateFormData.mobile_number}
                        onChange={(e) => setCandidateFormData({ ...candidateFormData, mobile_number: e.target.value })}
                        placeholder="91234567"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B5651D] focus:border-transparent"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Norsk mobilnummer (8 siffer). Må samsvare med kandidatens Vipps-profil.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-post <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={candidateFormData.email}
                        onChange={(e) => setCandidateFormData({ ...candidateFormData, email: e.target.value })}
                        placeholder="kandidat@example.com"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B5651D] focus:border-transparent"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Invitasjonslenke sendes til denne e-postadressen.
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Viktig: Vipps-verifisering
                      </h3>
                      <p className="text-sm text-amber-800">
                        E-post og mobilnummer må samsvare med kandidatens Vipps-profil.
                        Etter eksamen må kandidaten verifisere sin identitet med BankID via Vipps
                        for å hente kompetanseattesten.
                      </p>
                    </div>

                    <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-kf-blue-900 mb-2">Hvordan fungerer det?</h3>
                      <ol className="text-sm text-kf-blue-700 space-y-1 list-decimal list-inside">
                        <li>Kandidat registreres med kontaktinfo</li>
                        <li>Du sender invitasjon når prøven er klar</li>
                        <li>Kandidaten klikker lenken og tar prøven</li>
                        <li>Etter innlevering sendes kvittering automatisk</li>
                        <li>Kandidaten verifiserer med Vipps og henter attest</li>
                      </ol>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        type="submit"
                        disabled={registering}
                        className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                      >
                        {registering ? 'Registrerer...' : 'Registrer kandidat'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseRegistrationModal}
                        className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                      >
                        Avbryt
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-semibold mb-2">Kandidat registrert!</h2>
                    <p className="text-gray-600">{registeredCandidate.full_name}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Kandidatkode</div>
                      <div className="font-mono text-2xl font-bold text-gray-900 tracking-wider">
                        {registeredCandidate.candidate_code}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Del denne koden med kandidaten
                      </p>
                    </div>

                    <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
                      <div className="text-sm font-medium text-kf-blue-900 mb-2">Prøvelenke</div>
                      <div className="font-mono text-sm text-kf-blue-700 break-all mb-3">
                        {registeredCandidate.exam_url}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyExamLink(registeredCandidate.exam_url)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 transition"
                        >
                          <Copy size={16} />
                          Kopier lenke
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(registeredCandidate.candidate_code);
                            toast.showSuccess('Kandidatkode kopiert!');
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          <Copy size={16} />
                          Kopier kode
                        </button>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="text-sm font-medium text-amber-900 mb-1">📋 Viktig informasjon</div>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• Kandidaten trenger koden eller lenken for å starte prøven</li>
                        <li>• Etter sensur vil kandidaten motta SMS/e-post</li>
                        <li>• Kompetanseattest hentes via Vipps-pålogging</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-6">
                    <button
                      onClick={() => {
                        setRegisteredCandidate(null);
                        setCandidateFormData({ full_name: '', date_of_birth: '', mobile_number: '', email: '' });
                      }}
                      className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition"
                    >
                      Registrer ny kandidat
                    </button>
                    <button
                      onClick={handleCloseRegistrationModal}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                    >
                      Lukk
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {!csvImportResult ? (
                <>
                  <h2 className="text-xl font-semibold mb-4">Importer kandidater fra CSV</h2>

                  <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium text-kf-blue-900 mb-2">CSV-format</h3>
                    <p className="text-sm text-kf-blue-700 mb-2">
                      Bruk semikolon (;) som skilletegn. Alle felt er påkrevd.
                    </p>
                    <code className="text-xs bg-kf-blue-100 p-2 rounded block">
                      navn;fødselsdato;mobilnummer;e-post<br/>
                      Ola Nordmann;1985-03-15;91234567;ola@test.no<br/>
                      Kari Hansen;1990-07-22;98765432;kari@test.no
                    </code>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Viktig: Vipps-verifisering
                    </h3>
                    <p className="text-sm text-amber-800">
                      E-post og mobilnummer må samsvare med kandidatens Vipps-profil.
                      Invitasjonslenke sendes til oppgitt e-post.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lim inn CSV-data
                    </label>
                    <textarea
                      value={csvData}
                      onChange={(e) => setCsvData(e.target.value)}
                      placeholder="Ola Nordmann;1985-03-15;91234567;ola@test.no"
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B5651D] focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleCsvImport}
                      disabled={importingCsv || !csvData.trim()}
                      className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                    >
                      {importingCsv ? 'Importerer...' : 'Importer kandidater'}
                    </button>
                    <button
                      onClick={handleCloseCsvModal}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                    >
                      Avbryt
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 ${csvImportResult.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <CheckCircle className={`w-10 h-10 ${csvImportResult.failed === 0 ? 'text-green-600' : 'text-yellow-600'}`} />
                    </div>
                    <h2 className="text-2xl font-semibold mb-2">Import fullført</h2>
                    <p className="text-gray-600">
                      {csvImportResult.imported} importert, {csvImportResult.failed} feilet
                    </p>
                  </div>

                  {csvImportResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-medium text-red-900 mb-2">Feil ved import</h3>
                      <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                        {csvImportResult.errors.map((err, i) => (
                          <li key={i}>Linje {err.line}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setCsvImportResult(null);
                        setCsvData('');
                      }}
                      className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition"
                    >
                      Importer flere
                    </button>
                    <button
                      onClick={handleCloseCsvModal}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                    >
                      Lukk
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirm Modal - Step 1 */}
      {showResetConfirmModal && candidateToReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Nullstille innlevert besvarelse?</h2>
              <p className="text-gray-600 mb-4">
                Vil du nullstille besvarelsen for <strong>{candidateToReset.full_name}</strong>?
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <strong>OBS - kan ikke angres!</strong>
                  <p className="mt-1">
                    Kandidaten vil få varsel på e-post at <strong>{currentUser?.full_name || 'en bruker'}</strong> i <strong>{course?.organization_name || 'organisasjonen'}</strong> har nullstilt besvarelsen {new Date().toLocaleString('nb-NO')}.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleResetConfirm}
                className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 transition"
              >
                Bekreft
              </button>
              <button
                onClick={handleResetCancel}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Tilbake
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Final Confirm Modal - Step 2 */}
      {showResetFinalConfirmModal && candidateToReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-red-900 mb-2">Er du sikker?</h2>
              <p className="text-gray-600">
                Dette kan ikke angres!
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <ul className="text-sm text-red-800 space-y-2">
                <li>• Alle svar fra kandidaten vil bli slettet</li>
                <li>• Sensur og resultat vil bli fjernet</li>
                <li>• Kandidaten må ta prøven på nytt</li>
                <li>• Kandidaten får varsel på e-post + SMS med begrunnelse</li>
              </ul>
            </div>

            {/* v6.3.5: Begrunnelse påkrevd — sendes til kandidat og loggføres */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Begrunnelse <span className="text-red-600">*</span>
                <span className="text-xs text-gray-500 ml-2">
                  ({resetReason.length} / min 30 tegn — sendes til kandidat)
                </span>
              </label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                rows={3}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${
                  resetReason.length < 30 ? 'border-amber-400' : 'border-green-400'
                }`}
                placeholder="F.eks. Kandidaten har bedt om nytt forsøk grunnet tekniske problemer under prøven / SEB-krasj under levering / ny sensur bestilt av kandidat etter klage"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleResetFinalConfirm}
                disabled={resettingSession || resetReason.trim().length < 30}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resettingSession ? 'Nullstiller...' : 'OK - Nullstill'}
              </button>
              <button
                onClick={handleResetCancel}
                disabled={resettingSession}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer for Manual Grading Modal */}
      {showTransferModal && candidateToTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserCheck className="w-10 h-10 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Overfør til sensor for manuell vurdering
              </h2>
              <p className="text-gray-600 text-sm">
                {candidateToTransfer.full_name}
              </p>
            </div>

            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 mb-4 text-sm text-kf-blue-900">
              <p className="font-medium mb-1">Bruk kun for kandidater som:</p>
              <ul className="list-disc list-inside space-y-0.5 text-kf-blue-800">
                <li>Ikke møtte på skriftlig prøve, men leverte prosjektoppgave/muntlig framføring</li>
                <li>Skal vurderes alternativt av sensor basert på annen dokumentasjon</li>
              </ul>
              <p className="mt-2 text-kf-blue-900">
                Handlingen sender kandidaten til sensor med begrunnelsen din. Sensor bruker deretter
                «Registrer alternativ vurdering»-flyten for å gi bestått/ikke bestått.
              </p>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Begrunnelse til sensor <span className="text-red-600">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                ({transferReason.length} / min 30 tegn — synlig for sensor + loggført)
              </p>
              <textarea
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                rows={5}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  transferReason.trim().length < 30 ? 'border-amber-400' : 'border-green-400'
                }`}
                placeholder="F.eks: Kandidat møtte ikke på skriftlig prøve 18.09.2026 pga sykdom. Leverte prosjektoppgave om Y til meg på e-post 20.09 og gjennomførte muntlig framføring 22.09 på org-lokale. Ber sensor vurdere basert på disse leveransene."
                disabled={transferring}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleTransferConfirm}
                disabled={transferring || transferReason.trim().length < 30}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? 'Overfører...' : 'Overfør til sensor'}
              </button>
              <button
                onClick={handleTransferCancel}
                disabled={transferring}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exclude Candidate Modal */}
      {showExcludeModal && candidateToExclude && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserMinus className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Ekskluder kandidat</h2>
              <p className="text-gray-600">
                {candidateToExclude.full_name} vil ikke kunne ta prøven.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Årsak (valgfritt)
              </label>
              <select
                value={excludeReason}
                onChange={(e) => setExcludeReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Velg årsak...</option>
                <option value="Møtte ikke opp">Møtte ikke opp</option>
                <option value="Syk">Syk</option>
                <option value="Trukket seg">Trukket seg</option>
                <option value="Annet">Annet</option>
              </select>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Tips:</strong> Du kan inkludere kandidaten igjen senere og sende ny invitasjon.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleExcludeCandidate}
                disabled={excludingCandidate === candidateToExclude.id}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {excludingCandidate === candidateToExclude.id ? 'Ekskluderer...' : 'Ekskluder'}
              </button>
              <button
                onClick={() => {
                  setShowExcludeModal(false);
                  setCandidateToExclude(null);
                  setExcludeReason('');
                }}
                disabled={excludingCandidate === candidateToExclude.id}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast (SMS/e-post) — modal */}
      {showBroadcastModal && courseId && (
        <BroadcastModal
          courseInstanceId={courseId}
          meetingUrl={(course as any)?.meeting_url || null}
          examWindowStart={(course as any)?.exam_window_start || null}
          recipientCounts={{
            enrolled: candidates.filter(c => !c.excluded && c.status !== 'self_cancelled' && c.status !== 'waitlist').length,
            waitlist: candidates.filter(c => c.status === 'waitlist' && !c.excluded).length,
            attendees: candidates.filter(c => c.attendance_marked === true && !c.excluded).length,
            no_shows: candidates.filter(c => c.status === 'no_show_pending_reschedule').length
          }}
          onClose={() => setShowBroadcastModal(false)}
          onSent={() => loadData()}
        />
      )}

      {/* Tilrettelegging — modal */}
      {accommodationCandidate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Tilrettelegging — {accommodationCandidate.name}
              </h2>
              <button
                onClick={() => setAccommodationCandidate(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Lukk"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6">
              <AccommodationManager
                sessionId={accommodationCandidate.sessionId}
                candidateId={accommodationCandidate.candidateId}
                candidateName={accommodationCandidate.name}
                onAccommodationSet={() => {
                  // Last data på nytt så evt. ny tilretteleggings-info reflekteres i tabellen
                  loadData();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
